import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, eachDayOfInterval, startOfWeek, addDays } from 'date-fns';
import { nl } from 'date-fns/locale';

const DAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export default function Beschikbaarheid() {
  const { currentUser } = useAuth();
  const [weken, setWeken] = useState([]);
  const [selected, setSelected] = useState({}); // { weekId: { date: bool } }
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Load open weeks
      const snap = await getDocs(collection(db, 'weken'));
      const openWeken = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(w => w.open)
        .sort((a, b) => a.startDatum.localeCompare(b.startDatum));
      setWeken(openWeken);

      // Load existing beschikbaarheid for this user
      const beschSnap = await getDocs(collection(db, 'beschikbaarheid'));
      const myBesch = {};
      beschSnap.docs.forEach(d => {
        const data = d.data();
        if (data.userId === currentUser.uid) {
          if (!myBesch[data.weekId]) myBesch[data.weekId] = {};
          myBesch[data.weekId][data.datum] = true;
        }
      });
      setSelected(myBesch);
      setLoading(false);
    }
    load();
  }, [currentUser.uid]);

  function getDaysForWeek(week) {
    const start = parseISO(week.startDatum);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }

  function toggleDay(weekId, date) {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelected(prev => {
      const weekSel = { ...(prev[weekId] || {}) };
      if (weekSel[dateStr]) delete weekSel[dateStr];
      else weekSel[dateStr] = true;
      return { ...prev, [weekId]: weekSel };
    });
    setSaved(false);
  }

  function selectWholeWeek(weekId, days) {
    const weekSel = {};
    days.forEach(d => { weekSel[format(d, 'yyyy-MM-dd')] = true; });
    setSelected(prev => ({ ...prev, [weekId]: weekSel }));
    setSaved(false);
  }

  function deselectWholeWeek(weekId) {
    setSelected(prev => ({ ...prev, [weekId]: {} }));
    setSaved(false);
  }

  function isWholeWeekSelected(weekId, days) {
    const weekSel = selected[weekId] || {};
    return days.every(d => weekSel[format(d, 'yyyy-MM-dd')]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Delete existing entries for this user
      const snap = await getDocs(collection(db, 'beschikbaarheid'));
      const toDelete = snap.docs.filter(d => d.data().userId === currentUser.uid);
      await Promise.all(toDelete.map(d => deleteDoc(d.ref)));

      // Write new entries
      const writes = [];
      for (const [weekId, days] of Object.entries(selected)) {
        for (const [datum, val] of Object.entries(days)) {
          if (val) {
            const docId = `${currentUser.uid}_${datum}`;
            writes.push(setDoc(doc(db, 'beschikbaarheid', docId), {
              userId: currentUser.uid,
              weekId,
              datum,
              savedAt: new Date().toISOString()
            }));
          }
        }
      }
      await Promise.all(writes);
      setSaved(true);
    } catch (e) {
      alert('Fout bij opslaan: ' + e.message);
    }
    setSaving(false);
  }

  if (loading) return <div style={{ color: 'var(--text-3)', padding: 32 }}>Laden...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📅 Beschikbaarheid</h1>
        <p className="page-subtitle">Geef aan wanneer je beschikbaar bent voor de kampen</p>
      </div>

      {weken.length === 0 ? (
        <div className="card card-padded" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Geen openstaande weken</h3>
          <p style={{ color: 'var(--text-3)', fontSize: 14 }}>De admin heeft nog geen weken opengesteld. Kom later terug.</p>
        </div>
      ) : (
        <>
          {weken.map(week => {
            const days = getDaysForWeek(week);
            const allSelected = isWholeWeekSelected(week.id, days);
            const weekSel = selected[week.id] || {};
            const selectedCount = Object.values(weekSel).filter(Boolean).length;

            return (
              <div key={week.id} className="card card-padded" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 17, fontWeight: 600 }}>{week.naam || `Week ${week.startDatum}`}</h3>
                    <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>
                      {format(parseISO(week.startDatum), 'd MMM', { locale: nl })} – {format(addDays(parseISO(week.startDatum), 6), 'd MMM yyyy', { locale: nl })}
                      {selectedCount > 0 && <span className="badge badge-green" style={{ marginLeft: 8 }}>{selectedCount} dag{selectedCount !== 1 ? 'en' : ''} geselecteerd</span>}
                    </div>
                  </div>
                  <button
                    className={`btn ${allSelected ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                    onClick={() => allSelected ? deselectWholeWeek(week.id) : selectWholeWeek(week.id, days)}
                  >
                    {allSelected ? 'Hele week afvinken' : 'Hele week selecteren'}
                  </button>
                </div>

                <div className="week-grid">
                  {days.map((day, i) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const isSelected = !!(weekSel[dateStr]);
                    return (
                      <div
                        key={dateStr}
                        className={`day-cell ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleDay(week.id, day)}
                      >
                        <span className="day-name">{DAYS_NL[i]}</span>
                        <span className="day-date">{format(day, 'd')}</span>
                        <span className="day-month">{format(day, 'MMM', { locale: nl })}</span>
                        {isSelected && <span style={{ fontSize: 16 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>

                {week.notitie && (
                  <div className="alert alert-warning" style={{ marginTop: 12 }}>
                    <span>ℹ</span> {week.notitie}
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
            {saved && <div className="alert alert-success"><span>✓</span> Beschikbaarheid opgeslagen!</div>}
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
              {saving ? 'Opslaan...' : '💾 Beschikbaarheid opslaan'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
